export type BillingPlan = 'monthly' | 'annual';
export type CheckoutState = 'active' | 'processing' | 'expired' | 'open' | 'needs_support';
export type BillingReturn = { kind: 'success'; sessionId: string } | { kind: 'cancelled' | 'portal' } | null;
export type CheckoutProgress = CheckoutState | 'pending' | 'unavailable';

export function readBillingNavigation(search: string): { plan: BillingPlan | null; returnTo: BillingReturn } {
  const params = new URLSearchParams(search);
  const selected = params.get('billingPlan');
  const plan = selected === 'monthly' || selected === 'annual' ? selected : null;
  const sessionId = params.get('session_id');
  if (params.get('checkout') === 'success' && sessionId && /^cs_[A-Za-z0-9_]{1,240}$/.test(sessionId)) {
    return { plan, returnTo: { kind: 'success', sessionId } };
  }
  if (params.get('checkout') === 'cancelled') return { plan, returnTo: { kind: 'cancelled' } };
  if (params.get('billing') === 'return') return { plan, returnTo: { kind: 'portal' } };
  return { plan, returnTo: null };
}

/** Only the authenticated API response decides activation, never the return URL. */
export async function pollCheckoutStatus(options: {
  check: (signal: AbortSignal) => Promise<CheckoutState>;
  onProgress: (status: CheckoutProgress) => void;
  onActive: () => Promise<void>;
  signal: AbortSignal;
  attempts?: number;
  delayMs?: number;
  requestTimeoutMs?: number;
  maxDurationMs?: number;
  wait?: (ms: number, signal: AbortSignal) => Promise<void>;
}): Promise<void> {
  const { signal, check, onProgress, onActive, attempts = 15, delayMs = 2000, requestTimeoutMs = 8000, maxDurationMs = 45_000 } = options;
  const deadline = Date.now() + maxDurationMs;
  const wait = options.wait ?? ((ms, parentSignal) => new Promise<void>(resolve => {
    const finish = () => { clearTimeout(timer); parentSignal.removeEventListener('abort', finish); resolve(); };
    const timer = setTimeout(finish, ms);
    parentSignal.addEventListener('abort', finish, { once: true });
    if (parentSignal.aborted) finish();
  }));
  let hadResponse = false;
  onProgress('processing');
  for (let attempt = 0; attempt < attempts && !signal.aborted && Date.now() < deadline; attempt += 1) {
    const request = new AbortController();
    const abort = () => request.abort();
    signal.addEventListener('abort', abort, { once: true });
    const timer = setTimeout(abort, Math.min(requestTimeoutMs, deadline - Date.now()));
    let rejectAborted: () => void = () => {};
    try {
      const status = await Promise.race([
        check(request.signal),
        new Promise<never>((_, reject) => {
          rejectAborted = () => reject(new Error('Billing status request timed out'));
          request.signal.addEventListener('abort', rejectAborted, { once: true });
          if (request.signal.aborted) rejectAborted();
        }),
      ]);
      if (signal.aborted) return;
      if (!['active', 'processing', 'expired', 'open', 'needs_support'].includes(status)) throw new Error('Unknown checkout status');
      hadResponse = true;
      if (status === 'active') {
        onProgress('active');
        // Verified activation remains true if a separate profile refresh is slow.
        // Let the account refresh continue without extending this bounded poll.
        void onActive().catch(() => {});
        return;
      }
      if (status === 'expired' || status === 'needs_support') { onProgress(status); return; }
      onProgress(status);
    } catch {
      if (signal.aborted) return;
      // A timeout does not establish that payment failed. Recheck without buying again.
    } finally {
      clearTimeout(timer);
      signal.removeEventListener('abort', abort);
      request.signal.removeEventListener('abort', rejectAborted);
    }
    if (attempt + 1 < attempts && !signal.aborted && Date.now() < deadline) await wait(Math.min(delayMs, deadline - Date.now()), signal);
  }
  if (!signal.aborted) onProgress(hadResponse ? 'pending' : 'unavailable');
}
